// Agent API service for frontend-backend communication

import { getAuthToken } from "../lib/api/client";
import {
  Agent,
  AgentCreateRequest,
  AgentUpdateRequest,
  AgentListResponse,
  AgentDetail,
  AgentTestRequest,
  AgentTestResponse,
  WorkflowListResponse,
} from "../types/agent";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

class AgentApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE}/admin/agents${endpoint}`;
    const token = await getAuthToken();

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.detail || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    return response.json();
  }

  async getAgents(params?: {
    page?: number;
    size?: number;
    agent_type?: string;
    is_active?: boolean;
    search?: string;
  }): Promise<AgentListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined)
      searchParams.set("page", params.page.toString());
    if (params?.size !== undefined)
      searchParams.set("size", params.size.toString());
    if (params?.agent_type) searchParams.set("agent_type", params.agent_type);
    if (params?.is_active !== undefined)
      searchParams.set("is_active", params.is_active.toString());
    if (params?.search) searchParams.set("search", params.search);

    const queryString = searchParams.toString();
    return this.request<AgentListResponse>(
      queryString ? `?${queryString}` : ""
    );
  }

  async getAgent(agentId: string): Promise<AgentDetail> {
    return this.request<AgentDetail>(`/${agentId}`);
  }

  async createAgent(data: AgentCreateRequest): Promise<Agent> {
    return this.request<Agent>("", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateAgent(agentId: string, data: AgentUpdateRequest): Promise<Agent> {
    return this.request<Agent>(`/${agentId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteAgent(agentId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/${agentId}`, {
      method: "DELETE",
    });
  }

  async testAgent(
    agentId: string,
    request: AgentTestRequest
  ): Promise<AgentTestResponse> {
    return this.request<AgentTestResponse>(`/${agentId}/test`, {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  async getAgentWorkflows(agentId: string): Promise<WorkflowListResponse> {
    return this.request<WorkflowListResponse>(`/${agentId}/workflows`);
  }
}

export const agentApi = new AgentApiService();

// React Query hooks for easier integration
export const useAgentsQuery = (
  params?: Parameters<typeof agentApi.getAgents>[0]
) => {
  return {
    queryKey: ["agents", params],
    queryFn: () => agentApi.getAgents(params),
  };
};

export const useAgentQuery = (agentId: string) => {
  return {
    queryKey: ["agents", agentId],
    queryFn: () => agentApi.getAgent(agentId),
    enabled: !!agentId,
  };
};

export const useAgentWorkflowsQuery = (agentId: string) => {
  return {
    queryKey: ["agents", agentId, "workflows"],
    queryFn: () => agentApi.getAgentWorkflows(agentId),
    enabled: !!agentId,
  };
};
